import React from 'react';
import { cn } from '../../utils/cn';

export interface RangeSliderProps {
  min: number;
  max: number;
  step: number;
  /** Upper bound as form string; '' = no limit (thumb sits at `max`). */
  high: string;
  onHighChange: (value: string) => void;
  /** Lower bound as form string; '' = no limit (thumb at `min`). Omit for a single-thumb slider. */
  low?: string;
  onLowChange?: (value: string) => void;
  /** Accessible names for the thumbs, e.g. "Warmmiete bis". */
  highLabel: string;
  lowLabel?: string;
  disabled?: boolean;
  className?: string;
}

function toNumber(value: string, fallback: number): number {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Native range input(s) on one track: one thumb for "bis", or two overlaid inputs for
 * "ab–bis". The ends mean "no limit" and map to '' so the form keeps its empty-string
 * convention. Styled in `styles/range-slider.css`.
 */
export const RangeSlider: React.FC<RangeSliderProps> = ({
  min,
  max,
  step,
  high,
  onHighChange,
  low,
  onLowChange,
  highLabel,
  lowLabel,
  disabled,
  className,
}) => {
  const dual = low !== undefined && onLowChange !== undefined;
  const highValue = Math.min(toNumber(high, max), max);
  const lowValue = dual ? Math.max(toNumber(low as string, min), min) : min;
  const pct = (v: number) => ((v - min) / (max - min)) * 100;

  return (
    <div
      className={cn('range-slider', dual && 'range-slider--dual', className)}
      style={{ '--range-from': `${pct(lowValue)}%`, '--range-to': `${pct(highValue)}%` } as React.CSSProperties}
    >
      {dual && (
        <input
          type="range"
          aria-label={lowLabel}
          min={min}
          max={max}
          step={step}
          value={lowValue}
          disabled={disabled}
          onChange={(e) => {
            const v = Math.min(Number(e.target.value), highValue);
            onLowChange!(v <= min ? '' : String(v));
          }}
        />
      )}
      <input
        type="range"
        aria-label={highLabel}
        min={min}
        max={max}
        step={step}
        value={highValue}
        disabled={disabled}
        onChange={(e) => {
          const v = Math.max(Number(e.target.value), lowValue);
          onHighChange(v >= max ? '' : String(v));
        }}
      />
    </div>
  );
};
